/**
 * Component Registry - Single unified registry for all client-side components
 *
 * Each component decides whether it needs React or not by importing it internally.
 * Simple interactions use plain JavaScript, complex state management uses React.
 *
 * Benefits:
 * - One place to register everything
 * - Self-contained components (each imports its own dependencies)
 * - Easier to understand and maintain
 */
import { devLogger } from "./dev-logger.util";
class ComponentRegistry {
    constructor() {
        this.components = new Map();
        this.loadedComponents = new Map();
    }
    register(name, initializer) {
        if (this.components.has(name)) {
            devLogger.warn(`Component "${name}" is already registered. Overwriting.`);
        }
        this.components.set(name, initializer);
    }
    async get(name) {
        // Check if already loaded
        if (this.loadedComponents.has(name)) {
            return this.loadedComponents.get(name);
        }
        const entry = this.components.get(name);
        if (!entry) {
            return undefined;
        }
        const isLazyLoader = typeof entry === "function" && entry.length === 0;
        if (isLazyLoader) {
            try {
                const result = entry();
                // Verify it returns a promise
                if (!result || typeof result.then !== "function") {
                    devLogger.error(`Lazy loader for "${name}" did not return a Promise`);
                    return undefined;
                }
                const module = await result;
                // Try to get the component from the module (default export or named export matching the component name)
                const component = module.default || module[name] || Object.values(module)[0];
                if (!component) {
                    devLogger.error(`Lazy loaded module for "${name}" does not export a valid component`);
                    return undefined;
                }
                this.loadedComponents.set(name, component);
                return component;
            }
            catch (error) {
                devLogger.error(`Failed to lazy load component "${name}":`, error);
                return undefined;
            }
        }
        // It's a direct initializer
        const initializer = entry;
        this.loadedComponents.set(name, initializer);
        return initializer;
    }
    getSync(name) {
        if (this.loadedComponents.has(name)) {
            return this.loadedComponents.get(name);
        }
        const entry = this.components.get(name);
        if (!entry) {
            return undefined;
        }
        const isLazyLoader = typeof entry === "function" && entry.length === 0;
        if (isLazyLoader) {
            devLogger.warn(`Component "${name}" is lazy loaded. Use async get() instead.`);
            return undefined;
        }
        return entry;
    }
    getAll() {
        return Array.from(this.components.keys());
    }
    clear() {
        this.components.clear();
    }
}
export const componentRegistry = new ComponentRegistry();
